import * as React from 'react';
import {
  Box,
  Icon,
  StackLayout,
  ParagraphText,
} from '@leagueplatform/genesis-core';
import type {
  GDSIconProps,
  GDSColor,
  GDSStatus,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import type { ControlComponent } from '@web-config-app/entity-form';
import { EntityFormControl } from '@web-config-app/entity-form';
import {
  SEVERITY_STATUS,
  isSeverityValue,
  type SeverityValue,
} from '../../constants/severity';

interface SeverityPresentation {
  glyph: GDSIconProps['icon'];
  iconTint: GDSColor;
  borderColor: string;
  backgroundColor: string;
}

/**
 * Icon glyph and status-family tokens keyed by Genesis `GDSStatus`. Colours
 * derive entirely from the Genesis status theme tokens (no hardcoded hex). The
 * `info` status uses the blue `highlight` family, `warning` the amber family,
 * and `error` the red `critical` family. A severity value is mapped to its
 * status via {@link SEVERITY_STATUS} (the same mapping the backend's
 * `mapToAlertBanner` uses), so the authoring preview matches the member app.
 */
const PRESENTATION_BY_STATUS: Partial<Record<GDSStatus, SeverityPresentation>> =
  {
    info: {
      glyph: 'statusInformationCircle2Filled',
      iconTint: '$highlightIcon',
      borderColor: '$highlightBorderDefault',
      backgroundColor: '$highlightBackgroundSubdued',
    },
    warning: {
      glyph: 'statusAlertFill',
      iconTint: '$warningIcon',
      borderColor: '$warningBorderDefault',
      backgroundColor: '$warningBackgroundSubdued',
    },
    error: {
      glyph: 'statusErrorStatusFilled',
      iconTint: '$criticalIcon',
      borderColor: '$criticalBorderDefault',
      backgroundColor: '$criticalBackgroundSubdued',
    },
  };

const getPresentation = (value: string): SeverityPresentation | undefined =>
  isSeverityValue(value)
    ? PRESENTATION_BY_STATUS[SEVERITY_STATUS[value]]
    : undefined;

interface SeverityCardOption {
  value: string;
  label: string;
}

export interface SeverityCardsProps {
  data: string | undefined;
  path: string;
  enabled: boolean;
  handleChange: (path: string, value: string | undefined) => void;
  enumOptions: SeverityCardOption[];
  /** Accessible name for the radio group (the field label). */
  groupLabel?: string;
  /** Optional resolved description copy, keyed by enum value. */
  descriptions?: Partial<Record<string, string>>;
}

/**
 * Presentational severity picker: a radio group of cards, one per enum option.
 *
 * - Selecting the already-selected card clears the value (matching the default
 *   select control's clear behaviour, which keeps AJV validation happy on
 *   optional fields).
 * - Implements the WAI-ARIA radio-group keyboard pattern: a single tab stop
 *   (roving tabindex anchored on the selected card, or the first card when
 *   nothing is selected) and arrow-key navigation that moves focus and
 *   selection together.
 */
export const SeverityCards: React.FC<SeverityCardsProps> = ({
  data,
  path,
  enabled,
  handleChange,
  enumOptions,
  groupLabel,
  descriptions,
}) => {
  const optionRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const idPrefix = React.useId();

  const selectedIndex = enumOptions.findIndex(
    (option) => option.value === data,
  );
  // Roving tabindex anchor: the selected card, or the first card when none is.
  const tabbableIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const moveSelection = (fromIndex: number, delta: number) => {
    const count = enumOptions.length;
    if (count === 0) return;
    const nextIndex = (fromIndex + delta + count) % count;
    optionRefs.current[nextIndex]?.focus();
    handleChange(path, enumOptions[nextIndex].value);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        moveSelection(index, 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        moveSelection(index, -1);
        break;
      default:
    }
  };

  return (
    <StackLayout
      orientation="horizontal"
      spacing="$two"
      horizontalAlignment="stretch"
      verticalAlignment="stretch"
      role="radiogroup"
      aria-label={groupLabel}
      css={{ width: '100%' }}
    >
      {enumOptions.map(({ value, label }, index) => {
        const presentation = getPresentation(value);
        const selected = data === value;
        const description = descriptions?.[value];
        const labelId = `${idPrefix}-label-${value}`;
        const descriptionId = `${idPrefix}-desc-${value}`;
        return (
          <Box
            key={value}
            ref={(element: HTMLButtonElement | null) => {
              optionRefs.current[index] = element;
            }}
            as="button"
            type="button"
            role="radio"
            aria-checked={selected}
            aria-labelledby={labelId}
            aria-describedby={description ? descriptionId : undefined}
            disabled={!enabled}
            tabIndex={index === tabbableIndex ? 0 : -1}
            onClick={() => handleChange(path, selected ? undefined : value)}
            onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) =>
              handleKeyDown(event, index)
            }
            css={{
              flex: 1,
              display: 'block',
              textAlign: 'left',
              padding: '$threeQuarters',
              borderRadius: '$medium',
              border: selected
                ? `$borderWidths$thin solid ${
                    presentation?.borderColor ?? '$onSurfaceBorderDefault'
                  }`
                : '$borderWidths$thin solid $onSurfaceBorderSubdued',
              backgroundColor: selected
                ? presentation?.backgroundColor ?? '$surfaceBackgroundSecondary'
                : '$surfaceBackgroundPrimary',
              cursor: enabled ? 'pointer' : 'not-allowed',
            }}
          >
            <StackLayout orientation="vertical" spacing="$oneAndHalf">
              <StackLayout orientation="horizontal" spacing="$half">
                {presentation ? (
                  <Icon
                    icon={presentation.glyph}
                    tint={presentation.iconTint}
                  />
                ) : null}
                <ParagraphText
                  id={labelId}
                  css={{ fontWeight: '$bold', marginBlockEnd: '$none' }}
                >
                  {label}
                </ParagraphText>
              </StackLayout>
              {description ? (
                <ParagraphText
                  id={descriptionId}
                  size="sm"
                  emphasis="subtle"
                  css={{ marginBlockEnd: '$none' }}
                >
                  {description}
                </ParagraphText>
              ) : null}
            </StackLayout>
          </Box>
        );
      })}
    </StackLayout>
  );
};

export const toCardOptions = (
  enumOptions: { value?: string; label?: string | number | boolean }[],
): SeverityCardOption[] =>
  enumOptions
    .filter(
      (
        option,
      ): option is { value: string; label?: string | number | boolean } =>
        typeof option.value === 'string',
    )
    .map(({ value, label }) => ({
      value,
      label: String(label ?? value),
    }));

/**
 * JSON Forms control: renders the alert-banner severity enum as a card picker.
 * Wraps the presentational {@link SeverityCards} in `EntityFormControl` so it
 * receives `data`/`handleChange`/`path`/`enumOptions`/`label` from JSON Forms,
 * and resolves the per-card description copy from the module's string overrides.
 */
export const SeverityInputControl: ControlComponent = (props) => {
  const { formatMessage } = useIntl();

  // Literal message ids (not a computed/templated id) so the keys are
  // greppable and static usage detection can match them.
  const descriptions: Record<SeverityValue, string> = {
    informational: formatMessage({
      id: 'ALERT_BANNER_SEVERITY_INFORMATIONAL_DESCRIPTION',
    }),
    warning: formatMessage({ id: 'ALERT_BANNER_SEVERITY_WARNING_DESCRIPTION' }),
    critical: formatMessage({
      id: 'ALERT_BANNER_SEVERITY_CRITICAL_DESCRIPTION',
    }),
  };

  return (
    <EntityFormControl
      {...props}
      renderControl={({
        data,
        handleChange,
        path,
        enabled,
        enumOptions,
        label,
      }) => (
        <SeverityCards
          data={data}
          path={path}
          enabled={enabled}
          handleChange={handleChange}
          enumOptions={toCardOptions(enumOptions ?? [])}
          groupLabel={label}
          descriptions={descriptions}
        />
      )}
    />
  );
};
